import type { ObjectMetadata } from '@olytic/foundation';

import { OBJECT_DEFS } from '../../objects/objectDefs';
import { createdByField, lastModifiedByField, listColumnsOf, lookup, type Row, schemaRecord, text } from './kit';

const def = OBJECT_DEFS.prices;

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type: Amount as money, Scheme and Period by their labels, Default as a
 * check box, From and Until as plain dates, the Product and Price Grid linked
 * to their records; any column not placed below appears in Other Fields
 * (MOTHER-313). Selling Model and Price Grid show as they are written
 * ("One-time", "Acme-100 by Size"), not as picklist values, so they name their
 * format or link and are not typed (criterion 8). Its name (product code and label or selling model) titles the
 * page, so it stays off it.
 */
const field = schemaRecord('prices', { hidden: ['name'] });

const details = [
  field('product_name', 'Product'),
  field('label', 'Label'),
  field('selling_model', 'Selling Model', { format: text }),
  field('scheme', 'Scheme'),
  field('period', 'Period'),
  field('unit_price', 'Amount'),
  field('price_grid_name', 'Price Grid', { link: lookup('price-grids', 'price_grid_id') }),
  field('is_default', 'Default'),
  field('effective_from', 'From'),
  field('effective_to', 'Until'),
  field('created_at', 'Created'),
  createdByField(),
  lastModifiedByField(),
];

/** A price: one way a product is sold (selling model, scheme, amount or Price Grid), one the default. Rationale: prices.md. */
export const prices: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Price',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'identity', fields: ['product_name', 'label', 'selling_model', 'scheme', 'period', 'unit_price', 'price_grid_name', 'is_default', 'effective_from', 'effective_to'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'last_modified_by_name'] },
  ],
  related: [],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'product_name', 'band'],
};
